import * as THREE from 'three';
import {
  chamferBox,
  plainBox,
  tubeZ,
  rodZ,
  pipeZ,
  picatinnyRail,
  mergeGeom,
  place,
  translate,
  rotate,
} from './primitives.js';
import {
  kit,
  assembleKit,
  rodX,
  triggerGuard,
  rivetRow,
  slingLoop,
  curvedMagazine,
  straightMagazine,
  drumMagazine,
  tubeMagazine,
  horizontalMagazine,
  ammoBelt,
  beltBox,
  pistolGrip,
  stockWrist,
  foregrip,
  handStop,
  stockCollapsible,
  stockFixedTube,
  stockSkeleton,
  stockFolding,
  stockWire,
  stockWood,
  stockAK,
  stockThumbhole,
  stockBullpup,
  hgMlok,
  hgQuad,
  hgRibbed,
  hgWood,
  hgHex,
  hgShroud,
  hgTube,
  barrel,
  gasSystem,
  muzzleBirdcage,
  muzzleBrake,
  muzzleSlant,
  muzzleSuppressor,
  muzzleBreacher,
  muzzleCompensator,
  muzzleFlashCan,
  muzzleTriLug,
  muzzleIon,
  muzzleThread,
  bipod,
  railLaser,
  carryHandle,
  boltCarrier,
  chargingHandle,
  trigger,
  mountMagazine,
} from './parts.js';
import { buildOptic, clearCone, glassSubtense } from './optics.js';
import { attachHands } from './hands.js';

/**
 * The weapon chassis.
 *
 * `buildFirearm(ctx, spec)` assembles one weapon from a spec object. Everything that
 * varies between the twenty-three weapons is a value in that spec; everything that is
 * the same — the order parts are built in, how the optic is mounted, where the hands
 * go, what the animation layer is handed — is here, once.
 *
 * The receiver is the exception that earns its own table. Receiver *shape* is what
 * makes an AK an AK and an M4 an M4: a length parameter cannot turn a flat-top upper
 * into a stamped body with a rounded dust cover, and pretending it can is how you get
 * twenty-three guns that all look like the same gun with different barrels. So there
 * are fourteen receiver styles, most shared by two or three weapons, and the spec
 * picks one and tunes it.
 */

/* -------------------------------------------------------------------------- */
/* shared sub-assemblies                                                       */
/* -------------------------------------------------------------------------- */

/**
 * Lower receiver: magwell, trigger group, controls, trigger guard. Shared by every
 * conventional layout, because below the bore axis a rifle is a rifle.
 */
function lowerReceiver(K, o = {}) {
  const bucket = o.bucket ?? 'metal';
  const z = o.z ?? 0.028;
  const w = o.w ?? 0.034;
  const h = o.h ?? 0.050;
  const len = o.len ?? 0.120;
  const y = o.y ?? -0.046;
  K.push(bucket, place(chamferBox(w, h, len, 0.0016), 0, y, z));

  const mw = o.magwell;
  if (mw) {
    K.push(bucket, place(chamferBox(mw.w ?? w + 0.003, mw.h ?? 0.064, mw.d ?? 0.046, 0.0022), 0, mw.y ?? -0.048, mw.z ?? -0.006));
    K.push(bucket, place(chamferBox((mw.w ?? w + 0.003) + 0.005, 0.009, (mw.d ?? 0.046) + 0.006, 0.0026), 0, (mw.y ?? -0.048) - (mw.h ?? 0.064) / 2 + 0.001, mw.z ?? -0.006));
    K.push(bucket, translate(rodX(0.0055, 0.017, 0.023, 8), 0, (mw.y ?? -0.048) + 0.014, (mw.z ?? -0.006) + 0.014)); // mag release
  }

  if (o.controls !== false) {
    K.push(bucket, place(chamferBox(0.006, 0.016, 0.020, 0.0012), -(w / 2 + 0.002), y + 0.010, z - 0.024)); // bolt catch
    K.push(bucket, translate(rodX(0.0062, -(w / 2 + 0.008), -(w / 2), 8), 0, y + 0.020, z + 0.030)); // selector spindle
    K.push(bucket, place(chamferBox(0.005, 0.010, 0.026, 0.0010), -(w / 2 + 0.007), y + 0.020, z + 0.036)); // selector lever
    K.push(bucket, translate(rodX(0.0042, -(w / 2 + 0.002), w / 2 + 0.002, 8), 0, y + 0.016, z + 0.058)); // takedown pin
    K.push(bucket, translate(rodX(0.0042, -(w / 2 + 0.002), w / 2 + 0.002, 8), 0, y + 0.018, z - 0.052));
  }

  const g = o.guard ?? {};
  K.push(bucket, translate(triggerGuard(g.front ?? 0.004, g.back ?? 0.062, g.top ?? -0.062, g.bottom ?? -0.092, g.width ?? 0.0075), 0, 0, 0));
  return { bottomY: y - h / 2 };
}

/** Ejection port, brass deflector and (optionally) a forward assist, on the right. */
function ejectionPort(K, o = {}) {
  const bucket = o.bucket ?? 'metal';
  const x = o.x ?? 0.0195;
  K.push(bucket, place(chamferBox(0.004, o.h ?? 0.024, o.len ?? 0.052, 0.0010), x, o.y ?? 0.004, o.z ?? -0.002));
  K.push(bucket, place(chamferBox(0.005, 0.014, 0.014, 0.0012), x + 0.001, (o.y ?? 0.004) + 0.012, (o.z ?? -0.002) + 0.028));
  if (o.forwardAssist) {
    K.push(bucket, translate(rodZ(0.0068, (o.z ?? -0.002) + 0.028, (o.z ?? -0.002) + 0.052, 10), x + 0.003, (o.y ?? 0.004) + 0.008, 0));
    K.push(bucket, place(chamferBox(0.010, 0.014, 0.008, 0.0012), x + 0.004, (o.y ?? 0.004) + 0.008, (o.z ?? -0.002) + 0.054));
  }
}

/* -------------------------------------------------------------------------- */
/* receiver styles                                                             */
/* -------------------------------------------------------------------------- */

const RECEIVERS = {
  /**
   * AR-15 / AR-10 flat-top upper: a slab-sided box with a rounded top shoulder and a
   * Picatinny rail running its whole length. The rail *is* the silhouette — an AR is
   * the flattest-topped rifle there is, and that unbroken line from charging handle to
   * gas block is what the eye reads first.
   */
  arUpper(K, o) {
    const w = o.w ?? 0.040;
    const h = o.h ?? 0.044;
    const len = o.len ?? 0.226;
    const z = o.z ?? -0.015;
    const railY = h / 2 + 0.0005;
    K.metal(place(chamferBox(w, h, len, 0.0014), 0, 0, z));
    K.metal(place(chamferBox(w * 0.65, 0.006, len * 0.97, 0.0010), 0, h / 2 + 0.0035, z));
    K.metal(translate(picatinnyRail(o.railLen ?? len * 0.87, 0.0212, o.railZ ?? z + len / 2 - 0.020, o.railTeeth), 0, railY, 0));
    ejectionPort(K, { x: w / 2 - 0.0005, y: 0.004, z: z + 0.013, forwardAssist: o.forwardAssist !== false });
    K.metal(place(chamferBox(w * 0.85, 0.020, 0.014, 0.0014), 0, 0.014, z + len / 2 + 0.005)); // rear takedown lug
    return { railY, railZ0: z - len / 2, railZ1: z + len / 2 };
  },

  /**
   * AK stamped receiver.
   *
   * Three things make an AK read as an AK from a hundred metres: the *rounded top
   * cover* rather than a flat rail, the rear trunnion block where the stock enters at
   * a downward rake, and the big paddle safety on the right flank. All three are here,
   * and the top cover is built as a lathed half-round rather than a box precisely
   * because a boxy cover reads as a generic rifle.
   */
  akBody(K, o) {
    const w = o.w ?? 0.038;
    const len = o.len ?? 0.230;
    const z = o.z ?? -0.020;
    const y = o.y ?? -0.006;
    // Lower trough: the stamped U the internals sit in.
    K.metal(place(chamferBox(w, 0.048, len, 0.0016), 0, y - 0.016, z));
    K.metal(place(chamferBox(w + 0.003, 0.010, len * 0.94, 0.0012), 0, y - 0.038, z)); // bottom rib
    // Rounded top cover: a half-cylinder with a ribbed crown, the AK's signature.
    const cover = tubeZ([[w / 2 - 0.001, z - len / 2 + 0.004], [w / 2 - 0.001, z + len / 2 - 0.004]], 10);
    K.metal(translate(cover, 0, y + 0.012, 0));
    K.metal(place(chamferBox(w * 0.30, 0.006, len * 0.86, 0.0010), 0, y + 0.012 + w / 2 - 0.002, z));
    for (let i = 0; i < 2; i++) {
      K.metal(place(chamferBox(w * 0.86, 0.007, 0.010, 0.0010), 0, y + 0.022, z - len * 0.30 + i * len * 0.58));
    }
    // Rear trunnion + the tang the stock bolts into, raked down.
    K.metal(place(chamferBox(w, 0.052, 0.036, 0.0018), 0, y - 0.014, z + len / 2 + 0.014));
    // Big right-side safety paddle — an AK's loudest control.
    K.metal(place(chamferBox(0.005, 0.062, 0.016, 0.0012), w / 2 + 0.002, y - 0.006, z + len / 2 - 0.048, 0.22, 0, 0));
    K.metal(translate(rodX(0.0050, w / 2 - 0.004, w / 2 + 0.005, 8), 0, y - 0.006, z + len / 2 - 0.052));
    ejectionPort(K, { x: w / 2 - 0.0005, y: y + 0.010, z: z - 0.010, h: 0.026, len: 0.060, forwardAssist: false });
    K.metal(rivetRow(4, [w / 2 - 0.0005, y - 0.030, z - len * 0.34], [w / 2 - 0.0005, y - 0.030, z + len * 0.30]));
    K.metal(rivetRow(4, [-(w / 2 - 0.0005), y - 0.030, z - len * 0.34], [-(w / 2 - 0.0005), y - 0.030, z + len * 0.30]));
    // Optional side rail (an AK mounts glass on the left, not the top).
    if (o.sideRail) {
      K.metal(place(chamferBox(0.008, 0.030, 0.062, 0.0014), -(w / 2 + 0.004), y - 0.004, z + len * 0.22));
      K.metal(place(chamferBox(0.012, 0.010, 0.070, 0.0012), -(w / 2 + 0.008), y + 0.008, z + len * 0.22));
    }
    // Rail over the dust cover, for the variants that carry an optic on top.
    const railY = o.railY ?? y + 0.012 + w / 2 + 0.0015;
    if (o.topRail !== false) {
      K.metal(translate(picatinnyRail(o.railLen ?? len * 0.55, 0.0212, o.railZ ?? z + len * 0.30, o.railTeeth), 0, railY, 0));
    }
    return { railY, railZ0: z - len / 2, railZ1: z + len / 2 };
  },

  /**
   * Extruded monolithic upper (SCAR / modern piston pattern): wide flat flanks with a
   * longitudinal groove, a full-length top rail that continues unbroken onto the
   * handguard, and a reciprocating charging handle slot down one side. Boxier and
   * squarer than an AR upper, which is exactly how you tell them apart.
   */
  extruded(K, o) {
    const w = o.w ?? 0.046;
    const h = o.h ?? 0.048;
    const len = o.len ?? 0.240;
    const z = o.z ?? -0.020;
    const bucket = o.bucket ?? 'metal';
    const railY = h / 2 + 0.0005;
    K.push(bucket, place(chamferBox(w, h, len, 0.0018), 0, 0, z));
    // The extrusion's flutes: two longitudinal grooves down each flank.
    for (const sx of [-1, 1]) {
      for (const dy of [0.012, -0.012]) {
        K.push(bucket, place(chamferBox(0.005, 0.008, len * 0.92, 0.0010), sx * (w / 2 - 0.001), dy, z));
      }
    }
    K.metal(translate(picatinnyRail(o.railLen ?? len * 0.94, 0.0212, o.railZ ?? z + len / 2 - 0.008, o.railTeeth), 0, railY, 0));
    // Charging-handle slot and knob on the left flank.
    K.push(bucket, place(chamferBox(0.004, 0.012, len * 0.5, 0.0008), -(w / 2 + 0.001), 0.010, z - len * 0.10));
    ejectionPort(K, { x: w / 2 - 0.0005, y: 0.004, z: z + 0.016, h: 0.028, len: 0.058, forwardAssist: false });
    // Folding-stock hinge boss at the rear — this family always folds.
    K.metal(place(chamferBox(w * 0.7, 0.040, 0.024, 0.0018), 0, -0.006, z + len / 2 + 0.010));
    return { railY, railZ0: z - len / 2, railZ1: z + len / 2 };
  },

  /**
   * Bullpup shell.
   *
   * The magazine sits *behind* the pistol grip and the receiver runs all the way to
   * the buttpad. Nothing else in the roster looks remotely like it, which is the whole
   * reason three of the twenty-three are bullpups. The body is built as one long
   * polymer shell with a raised comb, and the rail runs the entire top.
   */
  bullpup(K, o) {
    const bucket = o.bucket ?? 'poly';
    const w = o.w ?? 0.048;
    const len = o.len ?? 0.400;
    const z = o.z ?? 0.100; // centre of the shell, well behind the origin
    const railY = o.railY ?? 0.030;
    const b = [];
    b.push(place(chamferBox(w, 0.062, len, 0.0040), 0, -0.006, z));
    b.push(place(chamferBox(w * 0.86, 0.022, len * 0.92, 0.0030), 0, 0.026, z)); // comb
    b.push(place(chamferBox(w * 0.72, 0.030, 0.090, 0.0035), 0, -0.040, z - len * 0.34)); // trigger-group swell
    // Cheek relief scallop on the left, so the shell is not a symmetric brick.
    b.push(place(chamferBox(0.010, 0.026, len * 0.34, 0.0025), -(w / 2 - 0.002), 0.020, z + len * 0.10));
    K.push(bucket, mergeGeom(b));
    // Ejection port far back on the right — the bullpup tell nobody draws but everyone
    // notices when it is missing.
    K.metal(place(chamferBox(0.005, 0.030, 0.062, 0.0012), w / 2 - 0.0005, 0.000, z + len * 0.16));
    K.metal(place(chamferBox(0.008, 0.014, 0.016, 0.0012), w / 2 + 0.001, 0.014, z + len * 0.16 + 0.036));
    // Full-length top rail on a riser: a bullpup's optic has to clear the comb.
    K.metal(place(chamferBox(w * 0.60, railY - 0.037, len * 0.80, 0.0016), 0, (railY - 0.037) / 2 + 0.037, z - len * 0.04));
    K.metal(translate(picatinnyRail(o.railLen ?? len * 0.78, 0.0212, o.railZ ?? z + len * 0.34, o.railTeeth), 0, railY, 0));
    return { railY, railZ0: z - len / 2, railZ1: z + len / 2 };
  },

  /**
   * Round stamped subgun receiver (MP5 pattern). A tube, not a box — and that circular
   * cross-section, plus the cocking tube standing off the left of the barrel shroud, is
   * the whole identity of the design.
   */
  smgTube(K, o) {
    const r = o.r ?? 0.0215;
    const z0 = o.z0 ?? -0.120;
    const z1 = o.z1 ?? 0.054;
    K.metal(translate(tubeZ([[0.0, z0], [r, z0 + 0.002], [r, z1 - 0.002], [0.0, z1]], 16), 0, 0.001, 0));
    K.metal(place(chamferBox(0.030, 0.012, (z1 - z0) * 0.96, 0.0012), 0, r * 0.93, (z0 + z1) / 2 - 0.014));
    const railY = r * 0.93 + 0.0065;
    K.metal(translate(picatinnyRail(o.railLen ?? (z1 - z0) * 0.75, 0.0196, o.railZ ?? z1 - 0.018, o.railTeeth), 0, railY, 0));
    ejectionPort(K, { x: r - 0.001, y: 0.006, z: z0 + 0.110, h: 0.020, len: 0.044, forwardAssist: false });
    // Cocking tube: forward, high and on the left. Angled up at the front.
    K.metal(translate(rodZ(0.0088, o.cockZ ?? -0.220, z0, 10), -(r - 0.001), 0.014, 0));
    K.metal(place(chamferBox(0.014, 0.014, 0.026, 0.0014), -(r - 0.001), 0.014, (o.cockZ ?? -0.220) - 0.006));
    return { railY, railZ0: z0, railZ1: z1 };
  },

  /**
   * Polymer PDW shell: a one-piece moulded body with the top rail cast in, a thumbhole
   * behind the grip and a fat, square profile. Reads as "modern compact" against the
   * MP5's round steel.
   */
  smgPoly(K, o) {
    const bucket = o.bucket ?? 'poly';
    const w = o.w ?? 0.042;
    const h = o.h ?? 0.052;
    const len = o.len ?? 0.210;
    const z = o.z ?? -0.030;
    const railY = h / 2 + 0.0055;
    const b = [];
    b.push(place(chamferBox(w, h, len, 0.0035), 0, 0, z));
    b.push(place(chamferBox(w * 0.92, 0.014, len * 0.9, 0.0025), 0, h / 2 + 0.004, z));
    // Angled facets down the flanks: the moulded look, and cheap.
    for (const sx of [-1, 1]) {
      b.push(place(chamferBox(0.010, 0.026, len * 0.72, 0.0022), sx * (w / 2 - 0.002), -0.012, z, 0, 0, sx * 0.42));
    }
    K.push(bucket, mergeGeom(b));
    K.metal(translate(picatinnyRail(o.railLen ?? len * 0.88, 0.0196, o.railZ ?? z + len / 2 - 0.008, o.railTeeth), 0, railY, 0));
    ejectionPort(K, { x: w / 2 - 0.0005, y: 0.004, z: z + len * 0.22, h: 0.022, len: 0.046, forwardAssist: false });
    if (o.sideCharge) {
      K.metal(place(chamferBox(0.014, 0.014, 0.034, 0.0014), -(w / 2 + 0.004), 0.014, z + len * 0.30));
    }
    return { railY, railZ0: z - len / 2, railZ1: z + len / 2 };
  },

  /**
   * Stamped box receiver with the magazine feeding *through the pistol grip* — the Uzi
   * layout. That single decision moves the magazine 90 mm back from where every other
   * weapon in the roster puts it, and no other silhouette cue in the game is that
   * cheap or that unmistakable.
   */
  stampedBox(K, o) {
    const w = o.w ?? 0.044;
    const h = o.h ?? 0.052;
    const len = o.len ?? 0.180;
    const z = o.z ?? -0.020;
    K.metal(place(chamferBox(w, h, len, 0.0016), 0, 0.002, z));
    // Pressed stiffening flutes down both flanks — the Uzi's diagonal ribs.
    for (const sx of [-1, 1]) {
      for (let i = 0; i < 3; i++) {
        K.metal(place(chamferBox(0.005, 0.012, 0.052, 0.0010), sx * (w / 2 - 0.001), -0.006, z - len * 0.26 + i * len * 0.26));
      }
    }
    // Top charging rib, running down the centre of the cover.
    K.metal(place(chamferBox(0.020, 0.010, len * 0.86, 0.0012), 0, h / 2 + 0.004, z));
    const railY = h / 2 + 0.010;
    K.metal(translate(picatinnyRail(o.railLen ?? len * 0.52, 0.0196, o.railZ ?? z + len * 0.28, o.railTeeth), 0, railY, 0));
    ejectionPort(K, { x: w / 2 - 0.0005, y: 0.008, z: z + len * 0.16, h: 0.020, len: 0.040, forwardAssist: false });
    return { railY, railZ0: z - len / 2, railZ1: z + len / 2 };
  },

  /**
   * Retro subgun: a round receiver with a bolt handle on *top*, a finned barrel and
   * wooden furniture. Everything about it is 1928 and it should look it.
   */
  retroTube(K, o) {
    const r = o.r ?? 0.0230;
    const z0 = o.z0 ?? -0.130;
    const z1 = o.z1 ?? 0.070;
    K.metal(translate(tubeZ([[0.0, z0], [r, z0 + 0.003], [r, z1 - 0.003], [0.0, z1]], 14), 0, 0, 0));
    // The blued top strap and its bolt slot.
    K.blued(place(chamferBox(0.024, 0.012, (z1 - z0) * 0.86, 0.0014), 0, r * 0.94, (z0 + z1) / 2));
    K.metal(place(chamferBox(0.030, 0.030, 0.052, 0.0018), 0, -r * 0.55, z1 - 0.040)); // trigger housing boss
    ejectionPort(K, { x: r - 0.001, y: 0.004, z: z0 + 0.086, h: 0.022, len: 0.048, forwardAssist: false });
    const railY = r * 0.94 + 0.0065;
    if (o.topRail) K.metal(translate(picatinnyRail(0.070, 0.0196, z1 - 0.020), 0, railY, 0));
    return { railY, railZ0: z0, railZ1: z1 };
  },

  /**
   * Top-loading bullpup shell (P90 pattern): one sweeping polymer body with the
   * magazine lying flat along the top, a thumbhole grip moulded into the front and a
   * shoulder pad formed by the shell itself. It is the least gun-shaped thing in the
   * roster and it should be.
   */
  p90Shell(K, o) {
    const bucket = o.bucket ?? 'poly';
    const len = o.len ?? 0.330;
    const z = o.z ?? 0.060;
    const w = o.w ?? 0.052;
    const b = [];
    b.push(place(chamferBox(w, 0.070, len, 0.0060), 0, -0.014, z));
    // The sweeping upper deck the magazine lies in: a shallow trough with a lip.
    b.push(place(chamferBox(w * 0.82, 0.016, len * 0.86, 0.0035), 0, 0.026, z - 0.010));
    for (const sx of [-1, 1]) {
      b.push(place(chamferBox(0.008, 0.022, len * 0.86, 0.0025), sx * (w * 0.41), 0.030, z - 0.010));
    }
    // Front thumbhole surround and the finger shelf under the muzzle.
    b.push(place(chamferBox(w * 0.86, 0.058, 0.030, 0.0045), 0, -0.052, z - len * 0.40));
    b.push(place(chamferBox(w * 0.90, 0.020, 0.040, 0.0035), 0, -0.078, z - len * 0.40));
    // Rear shoulder pad, formed by the shell.
    b.push(place(chamferBox(w * 0.92, 0.088, 0.024, 0.0045), 0, -0.010, z + len / 2 + 0.010));
    K.push(bucket, mergeGeom(b));
    K.rubber(place(chamferBox(w * 0.90, 0.086, 0.012, 0.0035), 0, -0.010, z + len / 2 + 0.026));
    // Downward ejection chute behind the grip — the other half of the P90 idea.
    K.metal(place(chamferBox(0.020, 0.014, 0.036, 0.0014), 0, -0.048, z - len * 0.24));
    const railY = o.railY ?? 0.056;
    K.metal(place(chamferBox(0.030, 0.012, 0.070, 0.0016), 0, railY - 0.008, z - len * 0.30));
    return { railY, railZ0: z - len / 2, railZ1: z + len / 2 };
  },

  /** Pump shotgun receiver: a deep flat-sided box with a loading gate underneath. */
  pumpReceiver(K, o) {
    const w = o.w ?? 0.044;
    const h = o.h ?? 0.058;
    const len = o.len ?? 0.196;
    const z = o.z ?? 0.014;
    const y = o.y ?? -0.014;
    K.metal(place(chamferBox(w, h, len, 0.0022), 0, y, z));
    K.metal(place(chamferBox(w + 0.002, 0.014, len * 0.72, 0.0018), 0, y + 0.028, z - 0.006)); // top strap
    K.metal(place(chamferBox(0.006, 0.026, 0.070, 0.0012), w / 2 - 0.0005, y + 0.004, z - 0.008)); // ejection port
    K.metal(place(chamferBox(w * 0.9, 0.012, 0.062, 0.0016), 0, y - h / 2 + 0.001, z - 0.034)); // loading gate
    K.metal(translate(rodX(0.0060, -(w / 2 + 0.002), w / 2 + 0.002, 8), 0, y - 0.024, z + len / 2 - 0.014)); // safety
    const railY = y + h / 2 + 0.0075;
    if (o.topRail !== false) {
      K.metal(translate(picatinnyRail(o.railLen ?? len * 0.58, 0.0196, o.railZ ?? z + len * 0.28, o.railTeeth), 0, railY, 0));
    }
    return { railY, railZ0: z - len / 2, railZ1: z + len / 2 };
  },

  /**
   * Break-action side-by-side: two barrels, a hinge, and a top lever. There is no
   * receiver above the bore at all, which is why this weapon reads as half a gun.
   */
  breakAction(K, o) {
    const z = o.z ?? 0.030;
    const spacing = o.spacing ?? 0.0148;
    K.metal(place(chamferBox(0.052, 0.050, 0.086, 0.0026), 0, -0.014, z)); // standing breech
    K.metal(place(chamferBox(0.046, 0.024, 0.030, 0.0022), 0, 0.006, z - 0.052)); // hinge knuckle
    K.metal(translate(rodX(0.0068, -0.024, 0.024, 10), 0, -0.014, z - 0.052));
    /**
     * Top lever, swung to the right — the tell that says "break action" instantly.
     *
     * It sits in a slot in the receiver at y = 0.008, *below* the barrel rib the sight
     * bead runs on. At 0.016 it stood proud of the rib line and was a solid bar
     * straight across the sight picture: this weapon measured a 0.00-degree clear
     * aperture entirely because of one control being 8 mm too tall.
     */
    K.metal(place(chamferBox(0.038, 0.008, 0.026, 0.0014), 0.008, 0.0080, z + 0.020, 0, 0.30, 0));
    K.metal(place(chamferBox(0.012, 0.010, 0.014, 0.0012), 0.024, 0.0080, z + 0.026));
    // Exposed hammer spurs, kept under the rib for the same reason.
    for (const sx of [-1, 1]) {
      K.metal(place(chamferBox(0.008, 0.020, 0.012, 0.0012), sx * spacing, 0.0120, z + 0.036, -0.5, 0, 0));
    }
    return { railY: 0.020, railZ0: z - 0.043, railZ1: z + 0.043, spacing };
  },

  /**
   * Belt-fed receiver: a deep body with a hinged feed tray cover on top. The cover is
   * the mounting surface, so the rail sits much higher than on any magazine-fed
   * weapon — which is why a SAW's optic looks like it is on stilts, and why the sight
   * height on this family has to be checked against the carry handle below it.
   */
  beltFed(K, o) {
    const w = o.w ?? 0.052;
    const len = o.len ?? 0.300;
    const z = o.z ?? -0.040;
    K.metal(place(chamferBox(w, 0.058, len, 0.0018), 0, -0.004, z));
    K.metal(place(chamferBox(w - 0.002, 0.018, len * 0.67, 0.0016), 0, 0.030, z - 0.030)); // feed tray cover
    K.metal(translate(rodX(0.0055, -0.026, 0.026, 8), 0, 0.030, z - len * 0.43)); // cover hinge
    K.metal(place(chamferBox(0.030, 0.016, 0.020, 0.0014), 0, 0.030, z + len * 0.22)); // cover latch
    K.metal(place(chamferBox(0.014, 0.010, 0.026, 0.0012), 0.024, 0.030, z + len * 0.21));
    const railY = o.railY ?? 0.0375;
    K.metal(translate(picatinnyRail(o.railLen ?? 0.190, 0.0212, o.railZ ?? z + len * 0.20, o.railTeeth ?? 14), 0, railY, 0));
    K.metal(place(chamferBox(0.005, 0.032, 0.078, 0.0012), w / 2 + 0.0005, -0.008, z + 0.012)); // ejection port
    K.metal(place(chamferBox(0.008, 0.014, 0.016, 0.0012), w / 2 + 0.0015, 0.012, z + 0.054));
    K.metal(place(chamferBox(0.006, 0.040, 0.062, 0.0014), -(w / 2 + 0.0005), -0.012, z - 0.036)); // feed chute
    K.metal(place(chamferBox(0.010, 0.014, 0.020, 0.0012), -(w / 2 + 0.002), 0.010, z - 0.068));
    for (let i = 0; i < 2; i++) {
      K.metal(place(chamferBox(w + 0.004, 0.010, 0.012, 0.0012), 0, -0.020, z - 0.086 + i * 0.100));
    }
    K.metal(place(chamferBox(w - 0.006, 0.048, 0.028, 0.0016), 0, -0.006, z + len / 2 + 0.008)); // buffer housing
    return { railY, railZ0: z - len / 2, railZ1: z + len / 2 };
  },

  /**
   * Classic milled rifle receiver with a wooden stock through it — no pistol grip, no
   * rail, an open-topped action and a side scope mount. The absence of modern
   * furniture is the silhouette.
   */
  classicRifle(K, o) {
    const w = o.w ?? 0.040;
    const len = o.len ?? 0.230;
    const z = o.z ?? -0.010;
    K.blued(place(chamferBox(w, 0.046, len, 0.0020), 0, 0.002, z));
    K.blued(place(chamferBox(w * 0.72, 0.012, len * 0.80, 0.0014), 0, 0.028, z - 0.012)); // top of the action
    K.blued(place(chamferBox(0.005, 0.026, 0.076, 0.0012), w / 2 - 0.0005, 0.008, z - 0.014)); // open port
    K.blued(place(chamferBox(0.030, 0.020, 0.028, 0.0016), 0, 0.018, z + len / 2 - 0.010)); // rear bridge
    // Side scope mount on the left, standing off the receiver wall.
    const railY = o.railY ?? 0.046;
    K.metal(place(chamferBox(0.010, 0.036, 0.070, 0.0016), -(w / 2 + 0.003), 0.024, z - 0.010));
    K.metal(place(chamferBox(0.026, 0.012, 0.078, 0.0014), -0.004, railY - 0.007, z - 0.010));
    K.metal(translate(picatinnyRail(0.072, 0.0196, z + 0.026), -0.004, railY, 0));
    return { railY, railZ0: z - len / 2, railZ1: z + len / 2 };
  },

  /**
   * Bolt-action chassis: a machined billet receiver with an integral 20-MOA rail and no
   * reciprocating parts to house, so it is flat-sided and square where a self-loader is
   * rounded. The bolt raceway down the right side is the tell.
   */
  boltChassis(K, o) {
    const w = o.w ?? 0.044;
    const h = o.h ?? 0.054;
    const len = o.len ?? 0.310;
    const z = o.z ?? -0.020;
    const railY = h / 2 + 0.001;
    K.metal(place(chamferBox(w, h, len, 0.0018), 0, 0, z));
    K.metal(place(chamferBox(w * 0.68, 0.008, len * 0.97, 0.0011), 0, h / 2 + 0.004, z));
    K.metal(translate(picatinnyRail(o.railLen ?? len * 0.97, 0.0212, o.railZ ?? z + len / 2 - 0.035, o.railTeeth ?? 20), 0, railY, 0));
    K.metal(place(chamferBox(0.005, 0.030, 0.086, 0.0011), w / 2 - 0.0005, 0.004, z)); // loading port
    K.metal(place(chamferBox(0.006, 0.020, 0.120, 0.0011), w / 2 - 0.0005, 0.014, z + 0.100)); // bolt raceway
    K.metal(place(chamferBox(w * 0.9, 0.020, 0.020, 0.0016), 0, 0.014, z + len / 2 + 0.018)); // shroud boss
    K.metal(translate(rodX(0.0044, -0.024, 0.024, 8), 0, -0.024, z - len * 0.28));
    K.metal(translate(rodX(0.0044, -0.024, 0.024, 8), 0, -0.024, z + len * 0.26));
    return { railY, railZ0: z - len / 2, railZ1: z + len / 2 };
  },
};

/* -------------------------------------------------------------------------- */
/* dispatch tables                                                             */
/* -------------------------------------------------------------------------- */

const STOCKS = {
  collapsible: stockCollapsible,
  fixedTube: stockFixedTube,
  skeleton: stockSkeleton,
  folding: stockFolding,
  wire: stockWire,
  wood: stockWood,
  ak: stockAK,
  thumbhole: stockThumbhole,
  bullpup: stockBullpup,
  none: () => ({ rearZ: 0.10 }),
};

const HANDGUARDS = {
  mlok: hgMlok,
  quad: hgQuad,
  ribbed: hgRibbed,
  wood: hgWood,
  hex: hgHex,
  shroud: hgShroud,
  tube: hgTube,
  none: () => ({ frontZ: -0.20, backZ: -0.10, r: 0.020 }),
};

const MUZZLES = {
  birdcage: muzzleBirdcage,
  brake: muzzleBrake,
  slant: muzzleSlant,
  suppressor: muzzleSuppressor,
  breacher: muzzleBreacher,
  compensator: muzzleCompensator,
  flashCan: muzzleFlashCan,
  triLug: muzzleTriLug,
  ion: muzzleIon,
  thread: muzzleThread,
};

/* -------------------------------------------------------------------------- */
/* the chassis                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Build one weapon from a spec.
 *
 * Order matters only in that the optic is built after everything it has to clear, so
 * `spec.optic.y` can be checked against the tallest thing forward of it. Everything
 * else is independent.
 */
export function buildFirearm(ctx, spec) {
  const K = kit(ctx);
  K.spec = spec;

  /* ------------------------------- receiver ------------------------------- */
  const rec = RECEIVERS[spec.receiver.style];
  if (!rec) throw new Error(`[weapons] no receiver style "${spec.receiver.style}"`);
  const R = rec(K, spec.receiver);

  if (spec.lower) lowerReceiver(K, spec.lower);

  /* --------------------------------- grip --------------------------------- */
  if (spec.grip) {
    if (spec.grip.style === 'wrist') stockWrist(K, spec.grip);
    else pistolGrip(K, spec.grip);
  }

  /* -------------------------------- stock --------------------------------- */
  let stockOut = { rearZ: 0.16 };
  if (spec.stock) {
    const fn = STOCKS[spec.stock.style];
    if (!fn) throw new Error(`[weapons] no stock style "${spec.stock.style}"`);
    stockOut = fn(K, spec.stock) ?? stockOut;
  }

  /* ------------------------------ handguard ------------------------------- */
  let hgOut = { frontZ: -0.30, backZ: -0.12, r: 0.022 };
  if (spec.handguard) {
    const fn = HANDGUARDS[spec.handguard.style];
    if (!fn) throw new Error(`[weapons] no handguard style "${spec.handguard.style}"`);
    hgOut = fn(K, spec.handguard) ?? hgOut;
  }
  if (spec.handguardRail) {
    // Tooth count is worth spending on a short rail and worth saving on a long one: a
    // 250 mm rail's slot pattern is read as a texture, not counted, and the triangles
    // buy more elsewhere.
    K.metal(translate(picatinnyRail(spec.handguardRail.len, 0.0212, spec.handguardRail.z, spec.handguardRail.teeth), 0, spec.handguardRail.y ?? R.railY, 0));
  }
  if (spec.barrelNut) {
    K.metal(translate(tubeZ([[spec.barrelNut.r, spec.barrelNut.z - 0.022], [spec.barrelNut.r, spec.barrelNut.z], [spec.barrelNut.r - 0.003, spec.barrelNut.z + 0.002]], 14), 0, 0, 0));
  }

  /* -------------------------------- barrel -------------------------------- */
  if (spec.barrel) barrel(K, spec.barrel);
  if (spec.gas) gasSystem(K, spec.gas);
  let muzzleTip = spec.muzzleTip ?? -0.44;
  if (spec.muzzle) {
    const fn = MUZZLES[spec.muzzle.style];
    if (!fn) throw new Error(`[weapons] no muzzle style "${spec.muzzle.style}"`);
    muzzleTip = (fn(K, spec.muzzle) ?? {}).tipZ ?? muzzleTip;
  }

  /* ------------------------------ accessories ----------------------------- */
  if (spec.foregrip) foregrip(K, spec.foregrip);
  if (spec.handStop) handStop(K, spec.handStop);
  if (spec.bipod) bipod(K, spec.bipod);
  if (spec.carryHandle) carryHandle(K, spec.carryHandle);
  if (spec.laser) railLaser(K, spec.laser);
  if (spec.sling) for (const s of spec.sling) K.metal(slingLoop(s[0], s[1], s[2]));

  /* ------------------------------ per-weapon ------------------------------ */
  // The escape hatch. Anything that genuinely does not generalise — a belt run, a
  // break-action's second barrel, a coil shroud — is written once, here, against the
  // kit. Keeping it explicit is better than growing the spec schema a field at a time
  // until nobody can tell which weapons use which half of it.
  if (spec.extras) spec.extras(K, { R, hgOut, stockOut, muzzleTip });

  /* --------------------------------- optic -------------------------------- */
  const optic = buildOptic(K, { railY: R.railY, ...spec.optic });

  /**
   * Rail-teeth clearance.
   *
   * A Picatinny rail's recoil lugs stand 7.7 mm above the rail's mounting plane, and
   * the rail keeps running *forward* past the optic on most of these weapons. If the
   * sight axis is not at least that far above the rail plus the optic's own radius,
   * the teeth clip the bottom of the sight picture somewhere down the handguard —
   * which is exactly what happened on four weapons here, measured as hits at model
   * y = railY + 0.0077 a hundred millimetres in front of the glass. Cheap to check,
   * invisible until someone aims.
   */
  const RAIL_TOOTH = 0.0077;
  const needY = R.railY + RAIL_TOOTH + (optic.topY - optic.sightY > 0 ? Math.min(optic.ocularR + 0.004, 0.030) : 0);
  if (optic.sightY < needY - 1e-4 && spec.optic.kind !== 'notch' && spec.optic.kind !== 'bead') {
    console.warn(
      `[weapons] ${spec.id}: sight axis ${optic.sightY.toFixed(4)} is below rail-tooth clearance ` +
        `${needY.toFixed(4)} (rail ${R.railY.toFixed(4)}) — the rail will clip the sight picture`
    );
  }

  /* ---------------------------- animated parts ---------------------------- */
  if (spec.bolt !== false) boltCarrier(K, spec.bolt ?? {});
  if (spec.charging) chargingHandle(K, spec.charging.style, spec.charging);
  trigger(K, spec.trigger ?? {});

  let magOffset = spec.magOffset ?? [0, -0.072, -0.006];
  if (spec.magazine) {
    const m = spec.magazine;
    let geom;
    if (m.style === 'curved') geom = curvedMagazine(m.w, m.d, m.segH, m.count, m.curve, m.chamfer).geom;
    else if (m.style === 'straight') geom = straightMagazine(m.w, m.d, m.len, m.chamfer, m.ribs).geom;
    else if (m.style === 'drum') geom = drumMagazine(m.r, m.w, m.neck).geom;
    else if (m.style === 'horizontal') geom = horizontalMagazine(m.len, m.w, m.h);
    else if (m.style === 'beltBox') {
      const parts = [beltBox(m.w, m.h, m.d)];
      geom = mergeGeom(parts);
    } else if (m.style === 'custom') geom = m.build(K);
    if (geom) {
      magOffset = m.offset ?? magOffset;
      const grp = mountMagazine(K, geom, magOffset, m.mat ?? 'poly');
      if (m.belt) {
        const belt = new THREE.Mesh(ammoBelt(m.belt.count, m.belt.from, m.belt.to, m.belt.sag), K.M.dark);
        belt.frustumCulled = false;
        grp.add(belt);
      }
    }
  }

  /* -------------------------------- assemble ------------------------------ */
  const root = assembleKit(K);
  for (const k of ['pump', 'bolt', 'charging', 'trigger', 'magazine', 'carrier', 'loadShell', 'bore', 'boreFront', 'lensFront', 'lensRear', 'scopeShadow', 'reticle']) {
    // Named as well as parented: the sight-line raycast reports which object it hit,
    // and "charging handle" is a far more useful answer than "(unnamed)".
    if (K.parts[k]) {
      K.parts[k].name = k;
      root.add(K.parts[k]);
    }
  }

  const hands = attachHands(ctx, root, K.parts, spec.hands);

  const aimPoint = new THREE.Vector3(0, optic.sightY, optic.aimZ);
  const out = {
    root,
    parts: K.parts,
    hands,
    reticleCollimated: optic.collimated,
    reticleClear: optic.reticleClear,
    aimPoint,
    muzzlePoint: new THREE.Vector3(0, spec.muzzleY ?? 0, muzzleTip),
    ejectPoint: new THREE.Vector3(...(spec.ejectPoint ?? [0.024, 0.006, -0.002])),
    ejectDir: new THREE.Vector3(...(spec.ejectDir ?? [0.86, 0.34, 0.38])),
    magOffset: new THREE.Vector3(...magOffset),
    // Kept on the model so the verification pass can read the numbers straight out of
    // the built weapon rather than re-deriving them from a spec it cannot see.
    optics: {
      apertures: optic.apertures,
      // The sight's own aiming mark — front post, bead, protective wings. Intended
      // on-axis geometry, reported separately from the unintended kind.
      marks: optic.marks ?? [],
      railY: R.railY,
      ocularR: optic.ocularR,
      ocularZ: optic.ocularZ,
      sightY: optic.sightY,
      aimZ: optic.aimZ,
      predictedCone: (ads) => clearCone(optic.apertures, optic.aimZ, ads),
      subtense: (ads) => glassSubtense(optic.ocularR, optic.ocularZ, optic.aimZ, ads),
    },
  };
  return out;
}

export { RECEIVERS, lowerReceiver, ejectionPort };
